'use client';

import { type ReactNode, useEffect, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { useWorkspaces } from '@air/plugin-ui';
import { useIsLoggedIn } from '@air/utils-auth';

import { WorkspaceLoadingState } from '~/app/workspace/_components/WorkspaceLoadingState';
import { WorkspaceSidebarShell } from '~/app/workspace/_components/WorkspaceSidebarShell';
import { ImportProgressStrip } from '~/components/ImportProgressStrip';
import { PremiereImportLifecycle } from '~/components/PremiereImportLifecycle';
import { useNavigationHistory } from '~/hooks/navigation/useNavigationHistory';
import { useConfigureThirdPartyServices } from '~/hooks/useConfigureThirdPartyServices';
import { useUnauthenticatedRedirect } from '~/hooks/useUnauthenticatedRedirect';
import { SIDEBAR_DEFAULT_WIDTH_PX, SIDEBAR_WIDTH_CSS_VAR } from '~/stores/useUiPreferencesStore';

const WorkspaceLayout = ({ children, modal }: { children: ReactNode; modal: ReactNode }) => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => setIsMounted(true), []);

  useUnauthenticatedRedirect();
  useConfigureThirdPartyServices();
  useNavigationHistory();

  const { isLoggedInFetched } = useIsLoggedIn();
  const { isLoading: isLoadingWorkspaces } = useWorkspaces();

  useIsomorphicLayoutEffect(() => {
    document.documentElement.style.setProperty(SIDEBAR_WIDTH_CSS_VAR, `${SIDEBAR_DEFAULT_WIDTH_PX}px`);
  }, []);

  const shouldShowLoading = !isMounted || !isLoggedInFetched || isLoadingWorkspaces;

  if (shouldShowLoading) {
    return <WorkspaceLoadingState />;
  }

  return (
    <main className="relative flex h-full bg-white dark:bg-neutral-1">
      <WorkspaceSidebarShell>{children}</WorkspaceSidebarShell>
      {modal}
      {/* Mounted at workspace level so import progress stays visible
          across gallery, review panel, and modal navigation. */}
      <PremiereImportLifecycle />
      <ImportProgressStrip />
    </main>
  );
};
export default WorkspaceLayout;
